import { useState } from "react";
import { Reveal } from "@/components/Reveal";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

export default function ForecastSplit() {
  const [amount, setAmount] = useState(6000);
  const [startIdx, setStartIdx] = useState(0);
  const [months, setMonths] = useState(12);
  const [stage, setStage] = useState("prop");
  const [wProposal, setWProposal] = useState(40);
  const [wNegotiation, setWNegotiation] = useState(70);

  const amt = Math.max(0, amount);
  const n = Math.min(12, Math.max(1, Math.round(months) || 1));
  const wp = Math.min(100, Math.max(0, wProposal));
  const wn = Math.min(100, Math.max(0, wNegotiation));
  const w = stage === "prop" ? wp / 100 : stage === "neg" ? wn / 100 : 0;

  const cents = Math.round(amt * 100);
  const base = Math.floor(cents / n);
  const rem = cents - base * n;
  const parts = Array.from({ length: n }, (_, i) => (base + (i === n - 1 ? rem : 0)) / 100);
  const max = Math.max(...parts, 1);

  const spill = Math.max(0, n - (12 - startIdx));
  const won = stage === "won";

  const inputCls = "w-full bg-paper border border-hair rounded px-2.5 py-2 font-mono text-[14px] text-coal outline-none focus:ring-2 focus:ring-coal/60";

  return (
    <section id="forecast" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-forecast">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">Forecast split</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">How one deal becomes monthly forecast records</h2>
        <p className="mt-2 text-[15px] text-fog max-w-3xl">
          Today a 12-month, $6,000 campaign shows up as $6,000 in the close month. Change
          the inputs to see how the custom code spreads it, and how the stage weighting
          applies.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-5 bg-white border border-hair rounded-md p-5 space-y-5" data-testid="forecast-panel">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="grid gap-1.5">
              <span className="font-mono text-[12px] text-fog">Line item amount ($)</span>
              <input type="number" min="0" step="100" value={amount} data-testid="forecast-amount-input" onChange={(e) => setAmount(+e.target.value || 0)} className={inputCls} />
            </label>
            <label className="grid gap-1.5">
              <span className="font-mono text-[12px] text-fog">Start month</span>
              <select value={startIdx} data-testid="forecast-start-select" onChange={(e) => setStartIdx(+e.target.value)} className={inputCls}>
                {MON.map((m, i) => (
                  <option key={m} value={i}>{m} 2027</option>
                ))}
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="font-mono text-[12px] text-fog">Months</span>
              <input type="number" min="1" max="12" value={months} data-testid="forecast-months-input" onChange={(e) => setMonths(+e.target.value || 1)} className={inputCls} />
            </label>
            <label className="grid gap-1.5">
              <span className="font-mono text-[12px] text-fog">Deal stage</span>
              <select value={stage} data-testid="forecast-stage-select" onChange={(e) => setStage(e.target.value)} className={inputCls}>
                <option value="prop">Proposal</option>
                <option value="neg">Negotiation</option>
                <option value="won">Closed Won</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-start">
            <label className="grid gap-1.5">
              <span className="font-mono text-[12px] text-fog">Proposal weight (%)</span>
              <input type="number" min="0" max="100" value={wp} data-testid="forecast-weight-proposal" onChange={(e) => setWProposal(+e.target.value || 0)} className={inputCls} />
            </label>
            <label className="grid gap-1.5">
              <span className="font-mono text-[12px] text-fog">Negotiation weight (%)</span>
              <input type="number" min="0" max="100" value={wn} data-testid="forecast-weight-negotiation" onChange={(e) => setWNegotiation(+e.target.value || 0)} className={inputCls} />
            </label>
            <p className="col-span-2 self-end text-[12.5px] text-fog leading-relaxed">
              Weights live in one settings record an admin can edit. A change applies on
              the next nightly run, with no code change.
            </p>
          </div>

          <div className="flex gap-4 flex-wrap text-[12px] text-fog" data-testid="forecast-legend">
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-[2px] bg-coal/25 inline-block" /> Raw monthly amount</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-[2px] bg-coal inline-block" /> Weighted, counts toward projected</span>
          </div>

          <div className="grid grid-cols-6 lg:grid-cols-12 gap-1.5" data-testid="forecast-months-grid">
            {MON.map((m, mi) => {
              const idx = mi - startIdx;
              const on = idx >= 0 && idx < n;
              const v = on ? parts[idx] : 0;
              return (
                <div key={m} className="grid gap-1 text-center" data-testid={`forecast-cell-${m.toLowerCase()}`}>
                  <div className={`h-16 relative rounded-[3px] overflow-hidden ${on ? "bg-sunk" : "bg-transparent border border-dashed border-hair"}`}>
                    {on && !won && (
                      <>
                        <span className="absolute inset-x-0 bottom-0 bg-coal/25" style={{ height: `${(v / max) * 100}%` }} />
                        <span className="absolute inset-x-0 bottom-0 bg-coal" style={{ height: `${(v / max) * w * 100}%` }} />
                      </>
                    )}
                  </div>
                  <span className="font-mono text-[10.5px] text-coal">{on && !won ? fmt(v) : "\u00a0"}</span>
                  <span className="font-mono text-[10.5px] text-fog">{m}</span>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 items-baseline" data-testid="forecast-summary">
            {won ? (
              <span><span className="font-display text-xl text-coal">0</span> <span className="text-[13px] text-fog">forecast records</span></span>
            ) : (
              <>
                <span><span className="font-display text-xl text-coal">{n}</span> <span className="text-[13px] text-fog">records</span></span>
                <span><span className="font-display text-xl text-coal">{fmt(parts[0])}</span> <span className="text-[13px] text-fog">/ month raw</span></span>
                <span><span className="font-display text-xl text-coal">{fmt(amt * w)}</span> <span className="text-[13px] text-fog">weighted total ({Math.round(w * 100)}%)</span></span>
              </>
            )}
          </div>

          <div className={`rounded px-3.5 py-3 text-[14px] leading-relaxed ${won ? "bg-sunk" : "bg-sunk"}`} data-testid="forecast-callout">
            {won
              ? "Closed Won: the forecast records are removed. This revenue now arrives from WideOrbit as booked, so the same dollars are never counted twice."
              : `${spill > 0 ? `${spill} month${spill > 1 ? "s" : ""} fall into 2028 and are created the same way. ` : ""}Any rounding goes into the last month, so the records always add up to the line item amount. If the deal changes, the records are rebuilt.`}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
